import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { withTranslation, type WithTranslation } from 'react-i18next';

interface OwnProps { children: React.ReactNode; fallbackMessage?: string; componentName?: string; }
type Props = OwnProps & WithTranslation;
interface State { hasError: boolean; }

class ErrorBoundaryInner extends React.Component<Props, State> {
  state: State = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // FB-018: Log with component context for diagnosability
    console.error(`[ErrorBoundary${this.props.componentName ? `:${this.props.componentName}` : ''}]`, error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Stack spacing={1} alignItems="center">
            <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
            <Typography variant="h6" color="error" gutterBottom>
              {this.props.fallbackMessage || t('errors:errorBoundary.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 500 }}>
              {t('errors:errorBoundary.body')}
            </Typography>
            {this.props.componentName && (
              <Typography variant="caption" color="text.disabled">
                {t('errors:errorBoundary.componentLabel', { name: this.props.componentName })}
              </Typography>
            )}
            <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
              {t('errors:errorBoundary.tryAgain')}
            </Button>
          </Stack>
        </Box>
      );
    }
    return this.props.children;
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryInner);
